/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import * as Sentry from '@sentry/react'

import config from 'env'
import { TestGroup } from 'types'
import { getDeviceDimension, getDeviceType } from 'utils'

// Change this to false if you want to reduce noise in staging
const ENABLE_STAGING_TRACKING_LOGS = config.env === 'staging'

/**
 * @module logger
 *
 * @summary Handle frontend monitoring through DataDog and Sentry. Uses console on local
 *
 * @method logger.debug | info | warn - different type of severity, report to datadog
 * @method report - function that report errors to datadog and sentry
 *
 */

type LogFunction = (message: string, data?: any) => void

interface Logger {
  debug: LogFunction
  info: LogFunction
  warn: LogFunction
  error: LogFunction
}

const logger: Logger = config.env === 'local' ? console : datadogLogs.logger

const containsResponse = (val: unknown): val is { response: unknown } => {
  const containsResponse = typeof val === 'object' && val && 'response' in val

  if (containsResponse) {
    return true
  }
  return false
}
const containsErrorResponse = (
  val: unknown,
): val is { response: { data: unknown; status: unknown } } => {
  const hasResponse = containsResponse(val)
  const valResponse = hasResponse && val.response
  const containsErrorResponse =
    hasResponse &&
    valResponse !== null &&
    typeof valResponse === 'object' &&
    'data' in valResponse &&
    'status' in valResponse
  if (containsErrorResponse) {
    return true
  }
  return false
}

interface ReportOptions {
  level?: 'error' | 'warning' | 'log'
  args?: Record<string, unknown>
}

/**
 * Reports an event or error, sending data to Datadog, logging it, and capturing exceptions with Sentry.
 * The function performs the following actions:
 * - Adds the error event to Datadog with the specified message and options.
 * - Logs the error message using a logger if the event does not contain an error response.
 * - Captures the exception with Sentry if the environment is not 'local'.
 * - If the event contains an error response, captures a detailed exception with Sentry, including response status and data.
 *
 * @example
 * // Basic usage with default level
 * report(new Error('Something went wrong'), 'An error occurred');
 *
 * @example
 * // Usage with custom level and additional arguments
 * report(event, 'A warning occurred', { level: 'warning', args: { userId: 123 } });
 */
const report = (
  event: unknown,
  readableMessage: string,
  options: ReportOptions = { level: 'error' },
) => {
  const { level, args } = options
  const error = event instanceof Error ? event : new Error(`${readableMessage}`)
  if (!error?.message) {
    error.message = readableMessage
  }
  datadogRum.addError(error, { message: readableMessage, args, level })

  if (!containsErrorResponse(event)) {
    logger.error(`${readableMessage}`, { args, level })
    if (config.env !== 'local') {
      Sentry.captureException(new Error(`${readableMessage}`, { cause: event }), {
        contexts: {
          error: {
            extra: {
              args,
            },
          },
        },
        level: level,
      })
    }
    return
  }

  if (config.env !== 'local') {
    const { status, data } = event.response

    Sentry.captureException(new Error(`${status} - ${readableMessage}`, { cause: event }), {
      contexts: {
        errorResponse: {
          data: {
            data,
          },
          status: {
            status,
          },
          extra: {
            args,
          },
        },
      },
      level: level,
    })
  }
}

const initAnalytics = (quoteId: string, traits?: Record<string, unknown>) => {
  datadogRum.setUser({
    quoteId,
  })

  datadogLogs.setGlobalContext({
    env: config.env,
  })

  Sentry.setUser({
    id: quoteId,
  })

  analytics.identify(traits)
}

const resetAnalytics = () => {
  Sentry.getCurrentScope().setUser(null)
  datadogRum.clearUser()
}

/**
 * @deprecated Use `useTracking` instead
 */
const track = (
  message: string,
  properties?: Record<string, any>,
  options?: SegmentAnalytics.SegmentOpts,
) => {
  if (ENABLE_STAGING_TRACKING_LOGS) {
    console.log(`EVENT │ Name: '${message}'`)

    if (properties) {
      console.log('      └─', properties)
    }
    if (options) {
      console.log('      └─', options)
    }
  }

  datadogRum.addAction(message, properties)

  const deviceDimension = getDeviceDimension()
  const deviceType = getDeviceType()
  analytics.track(message, { deviceType, deviceDimension, ...properties }, options)
}

const identify = (
  quoteId: string,
  traits: Record<string, any>,
  options?: SegmentAnalytics.SegmentOpts,
) => {
  analytics.identify(traits, options)

  Sentry.setContext(quoteId, traits)
  datadogLogs.setGlobalContextProperty(quoteId, traits)
  datadogRum.setGlobalContextProperty(quoteId, traits)
}

/**
 * @deprecated Use `usePageTracking` instead
 */
const trackPage = (
  pageName: string,
  properties?: Record<string, any>,
  options?: SegmentAnalytics.SegmentOpts,
) => {
  if (ENABLE_STAGING_TRACKING_LOGS) {
    console.log(`PAGE │ Name: '${pageName}'`)

    if (properties) {
      console.log('     └─', properties)
    }
    if (options) {
      console.log('      └─', options)
    }
  }

  datadogRum.addAction(`Viewed ${pageName} page`, properties)

  const deviceDimension = getDeviceDimension()
  const deviceType = getDeviceType()
  analytics.page(pageName, { deviceType, deviceDimension, ...properties }, options)
}

export const addDatadogFeatureFlagEvaluation = (
  featureFlagsToEvaluate: Record<string, boolean>,
) => {
  Object.keys(featureFlagsToEvaluate).forEach((flag) => {
    const active = featureFlagsToEvaluate[flag]
    datadogRum.addFeatureFlagEvaluation(flag, active)
  })
}

export const addDatadogAbTestEvaluation = (abTestsToEvaluate: Record<string, TestGroup>) => {
  Object.keys(abTestsToEvaluate).forEach((flag) => {
    const testGroup = abTestsToEvaluate[flag]
    datadogRum.addFeatureFlagEvaluation(flag, testGroup)
  })
}

export { identify, initAnalytics, logger, report, resetAnalytics, track, trackPage }
